import React, { useState } from "react";
import { RefreshCw, AlertTriangle } from "lucide-react";
import { api, getApiError } from "../../api/client";

function shortLabel(service) {
  const secretNames = service.payload?.secret_names;
  if (secretNames?.length > 0)
    return `${secretNames.length} secret ${secretNames.length === 1 ? "name" : "names"}`;
  if (service.evidence?.length > 0)
    return service.description.split(".")[0];
  return service.kind ?? "";
}

export default function RejectModal({ runId, services = [], scriptGenerated = false, onClose, onSuccess }) {
  const [removedNames, setRemovedNames] = useState(new Set());
  const [rationale, setRationale]       = useState("");
  const [submitting, setSubmitting]     = useState(false);
  const [error, setError]               = useState("");

  const toggle = (name) =>
    setRemovedNames((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const items = [...removedNames];
    const note  = rationale.trim();
    if (items.length === 0 && !note) {
      setError("Select at least one service to remove or provide a rationale.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await api.post(`/golive/proposals/${runId}/reject`, {
        rationale:       note || null,
        items_to_remove: items,
      });
      onSuccess();
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="gl-modal-overlay mt-21"  onClick={onClose}>
      <div
        className="gl-modal"
        style={{ width: 520, maxHeight: "90vh", overflowY: "auto" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div>
          <h3 className="gl-modal-title">Reject &amp; re-analyze</h3>
          <p className="text-sm text-slate-500 mt-1">
            Tell the AI what to change. The run goes back to{" "}
            <strong className="text-slate-700">analyzing</strong> with this feedback.
          </p>
        </div>

        {scriptGenerated && (
          <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
            <AlertTriangle size={15} className="text-red-500 shrink-0 mt-0.5" />
            <p className="text-sm text-red-700 leading-relaxed">
              The generated script will be <strong>discarded</strong>. The repository will be
              re-cloned and fully re-analyzed from scratch.
            </p>
          </div>
        )}

        {error && <div className="gl-error">{error}</div>}

        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          {/* Services checklist */}
          {services.length > 0 && (
            <div className="flex flex-col gap-1">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
                Mark items to remove
              </p>
              <div className="flex flex-col divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden">
                {services.map((svc) => {
                  const removed = removedNames.has(svc.name);
                  return (
                    <label
                      key={svc.name}
                      className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <input
                        type="checkbox"
                        className="w-4 h-4 accent-red-500 shrink-0 cursor-pointer"
                        checked={removed}
                        onChange={() => toggle(svc.name)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${removed ? "line-through text-slate-400" : "text-slate-800"}`}>
                          {svc.name}
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5 truncate">
                          {removed ? "Will be removed from proposal" : shortLabel(svc)}
                        </p>
                      </div>
                      <span className={`text-xs font-bold tracking-wider shrink-0 ${removed ? "text-red-500" : "text-slate-400"}`}>
                        {removed ? "REMOVE" : "KEEP"}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Free-text rationale */}
          <div className="flex flex-col gap-1.5">
            <p className="text-sm font-semibold text-slate-800">Anything to add or note?</p>
            <textarea
              className="gl-input text-sm"
              rows={4}
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              placeholder="We use Postgres for sessions, not Firestore — the README mention is outdated…"
              style={{ resize: "vertical" }}
            />
            <p className="text-xs text-slate-400">
              Free-text rationale is sent to the AI alongside the structural removals.
            </p>
          </div>

          <div className="gl-modal-actions">
            <button type="button" className="gl-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="gl-btn danger" disabled={submitting}>
              <RefreshCw size={14} />
              {submitting ? "Submitting…" : "Reject & re-analyze"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
